/**
 * 竞价模块：列表 / 详情 / 出价 / 状态轮询
 * ★ 架构 §9-1：/auctions 系列后端可能未实现 → USE_MOCK.auction 开关 + 失败自动降级 mock
 *   出价/防秒杀延长（IR-02）/状态在 mock 层完整模拟
 */
import { get, post } from './http';
import { canFallbackToMock } from './errors';
import { normalizeCattle } from './cattle';
import {
  mockAuctionStatus,
  mockGetAuction,
  mockListAuctions,
  mockPlaceBid,
  USE_MOCK,
} from './mock';
import type { Auction, AuctionBid, AuctionStatus, PlaceBidResult } from '@/types';

type BidStatus = 'leading' | 'outbid' | null;

/**
 * 归一化后端 my_bid_status：
 * 后端未出价时返回字符串 "null"（service.BidStatusNull），前端契约用 null。
 * 统一在此处转成 null，避免页面误把字符串 "null" 当作已出价状态。
 */
function normalizeBidStatus(value: unknown): BidStatus {
  if (value === 'leading' || value === 'outbid') return value;
  return null;
}

/** 状态轮询响应原始契约（my_bid_status 为 string|null） */
interface AuctionStatusRaw {
  id?: number;
  current_price: number;
  end_time: string;
  bid_count: number;
  status: number;
  my_bid_status: string | null;
  extended?: boolean;
  last_bid?: AuctionBid | null;
}

/** 竞价列表（status: 0-预告 1-进行中 2-已结束等） */
export async function listAuctions(status?: number): Promise<Auction[]> {
  if (USE_MOCK.auction) return mockListAuctions(status);
  try {
    const data = await get<{ list: Auction[] }>('/auctions', { status, page: 1, page_size: 100 });
    return (data.list ?? []).map((a) => ({
      ...a,
      cattle: a.cattle ? normalizeCattle(a.cattle) : a.cattle,
    }));
  } catch (e) {
    if (canFallbackToMock(e)) return mockListAuctions(status);
    throw e;
  }
}

/** 竞价详情（含出价记录） */
export async function getAuction(id: number): Promise<Auction> {
  if (USE_MOCK.auction) return mockGetAuction(id);
  try {
    const data = await get<Auction>(`/auctions/${id}`);
    return { ...data, cattle: data.cattle ? normalizeCattle(data.cattle) : data.cattle };
  } catch (e) {
    if (canFallbackToMock(e)) return mockGetAuction(id);
    throw e;
  }
}

/** 出价（IR-01 校验在 mock 层 / 后端同时进行） */
export async function placeBid(id: number, price: number): Promise<PlaceBidResult> {
  if (USE_MOCK.auction) return mockPlaceBid(id, price, 3);
  try {
    const raw = await post<PlaceBidResult>(`/auctions/${id}/bids`, { price });
    // 出价成功后端恒为 leading；此处兼容 outbid 与异常值，避免破坏前端类型
    return { ...raw, my_bid_status: raw.my_bid_status === 'outbid' ? 'outbid' : 'leading' };
  } catch (e) {
    if (canFallbackToMock(e)) return mockPlaceBid(id, price, 3);
    throw e;
  }
}

/** 竞价状态轮询（防秒杀延长刷新） */
export async function getAuctionStatus(id: number): Promise<AuctionStatus> {
  if (USE_MOCK.auction) return mockAuctionStatus(id, 3);
  try {
    const raw = await get<AuctionStatusRaw>(`/auctions/${id}/status`);
    // 后端状态 VO 不回传 id，此处用入参补齐，并对齐 my_bid_status 契约
    return {
      id,
      current_price: raw.current_price,
      end_time: raw.end_time,
      bid_count: raw.bid_count,
      status: raw.status,
      my_bid_status: normalizeBidStatus(raw.my_bid_status),
      extended: raw.extended,
      last_bid: raw.last_bid,
    };
  } catch (e) {
    if (canFallbackToMock(e)) return mockAuctionStatus(id, 3);
    throw e;
  }
}

/** 我的竞价场次（当前用户作为卖家，GET /auctions/mine，支撑「我的挂牌」合并两源） */
export async function listMyAuctions(): Promise<Auction[]> {
  try {
    const data = await get<{ list: Auction[] }>('/auctions/mine', { page: 1, page_size: 100 });
    return (data.list ?? []).map((a) => ({
      ...a,
      cattle: a.cattle ? normalizeCattle(a.cattle) : a.cattle,
    }));
  } catch (e) {
    // 无「我的」mock 语义：网络/404 降级时返回空数组，由调用方静默跳过竞价源，不伪造他人场次
    if (canFallbackToMock(e)) return [];
    throw e;
  }
}
